/*LIBRARY*/
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { connect } from "react-redux";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";

/*COMPONENT*/

/*REDUX*/

/*DATA*/
import { MODELS_DATA } from "./models-data";

/*CONST*/
import maplibregl from "maplibre-gl";

const LOAD_ZOOM_THRESHOLD = 16;
const OPACITY_THRESHOLD_ZOOM = 21;
const RESET_ZOOM_THRESHOLD = 8;
const MODEL_OPACITY = 0.5;

const gltfCache = new Map();

const MODEL_3D = ({ map, isModelHidden, properties, modelData }) => {
  const threeRef = useRef({
    scene: null,
    renderer: null,
    camera: null,
    model: null,
  });

  const isLoadedRef = useRef(false);
  const isLoadingRef = useRef(false);
  const currentOpacityRef = useRef(null);
  const movementRafRef = useRef(null);
  const materialsRef = useRef([]);

  const localTransformRef = useRef(null);

  const localTransform = useMemo(() => {
    const _mercator = maplibregl.MercatorCoordinate.fromLngLat(
      modelData.centerCoordinate,
      modelData.altitude,
    );
    const _scale =
      _mercator.meterInMercatorCoordinateUnits() * modelData.scaleMultiplier;

    const transform = new THREE.Matrix4()
      .makeTranslation(_mercator.x, _mercator.y, _mercator.z)
      .scale(new THREE.Vector3(_scale, -_scale, _scale))
      .multiply(
        new THREE.Matrix4().makeRotationAxis(
          new THREE.Vector3(1, 0, 0),
          Math.PI / 2,
        ),
      );

    if (modelData.rotationY) {
      transform.multiply(
        new THREE.Matrix4().makeRotationAxis(
          new THREE.Vector3(0, 1, 0),
          modelData.rotationY * (Math.PI / 180),
        ),
      );
    }

    return transform;
  }, [modelData]);

  localTransformRef.current = localTransform;

  const resetThreeState = useCallback(() => {
    const three = threeRef.current;

    if (movementRafRef.current) {
      cancelAnimationFrame(movementRafRef.current);
      movementRafRef.current = null;
    }

    if (three.model && three.scene) {
      three.scene.remove(three.model);
    }

    if (three.renderer) {
      three.renderer.dispose();
      three.renderer = null;
    }

    three.scene = null;
    three.camera = null;
    three.model = null;

    isLoadedRef.current = false;
    isLoadingRef.current = false;
    materialsRef.current = [];
    currentOpacityRef.current = null;
  }, []);

  const triggerRepaint = useCallback(() => {
    map?.triggerRepaint();
  }, [map]);

  const applyDynamicOpacity = useCallback(() => {
    if (!threeRef.current.model || !materialsRef.current.length) return;
    const zoom = map.getZoom();
    const targetOpacity = zoom < OPACITY_THRESHOLD_ZOOM ? 1.0 : MODEL_OPACITY;

    if (currentOpacityRef.current === targetOpacity) return;
    currentOpacityRef.current = targetOpacity;

    materialsRef.current.forEach((mat) => {
      mat.transparent = targetOpacity < 1.0;
      mat.opacity = targetOpacity;
      mat.depthWrite = targetOpacity >= 1.0;
      mat.needsUpdate = true;
    });

    triggerRepaint();
  }, [map, triggerRepaint]);

  useEffect(() => {
    if (!map) return;

    const three = threeRef.current;

    const customLayer = {
      id: modelData.layerId,
      type: "custom",
      renderingMode: "3d",

      onAdd(mapInstance, gl) {
        if (!three.scene) {
          three.camera = new THREE.Camera();
          three.scene = new THREE.Scene();

          const hemiLight = new THREE.HemisphereLight(0xfff5e0, 0x8090a0, 1.4);
          three.scene.add(hemiLight);

          const dirLight = new THREE.DirectionalLight(0xfff0e6, 1.6);
          dirLight.position.set(50, 70, 100);
          dirLight.castShadow = false;
          three.scene.add(dirLight);
        }

        if (!three.renderer || three.renderer.getContext() !== gl) {
          if (three.renderer) three.renderer.dispose();
          three.renderer = new THREE.WebGLRenderer({
            canvas: mapInstance.getCanvas(),
            context: gl,
            antialias: true,
            powerPreference: "high-performance",
            stencil: false,
            depth: true,
          });
          three.renderer.autoClear = false;
          three.renderer.shadowMap.enabled = false;
          three.renderer.setPixelRatio(1);
        }

        if (three.model && !three.scene.children.includes(three.model)) {
          three.scene.add(three.model);
        }
      },

      render(gl, matrix) {
        if (!three.renderer || !three.scene || !three.camera) return;

        const m = new THREE.Matrix4().fromArray(
          matrix.defaultProjectionData.mainMatrix,
        );
        three.camera.projectionMatrix = m.multiply(localTransformRef.current);
        three.renderer.resetState();
        three.renderer.render(three.scene, three.camera);
      },
    };

    const load3DModel = () => {
      if (isLoadedRef.current || isLoadingRef.current) return;
      isLoadingRef.current = true;

      const applyModel = (gltfScene) => {
        const clonedScene = gltfScene.clone(true);

        clonedScene.traverse((child) => {
          if (!child.isMesh) return;
          child.castShadow = false;
          child.receiveShadow = false;
          const mats = [].concat(child.material);
          mats.forEach((mat) => {
            mat.fog = false;
            mat.dithering = false;
            materialsRef.current.push(mat);
          });
        });

        const box = new THREE.Box3().setFromObject(clonedScene);
        const center = box.getCenter(new THREE.Vector3());
        clonedScene.position.x = -center.x;
        clonedScene.position.y = -box.min.y;
        clonedScene.position.z = -center.z;

        three.model = clonedScene;
        applyDynamicOpacity();

        if (three.scene) {
          three.scene.add(clonedScene);
          triggerRepaint();
        }

        isLoadedRef.current = true;
        isLoadingRef.current = false;
      };

      if (gltfCache.has(modelData.url)) {
        applyModel(gltfCache.get(modelData.url));
        return;
      }

      const loader = new GLTFLoader();
      loader.load(
        modelData.url,
        (gltf) => {
          gltfCache.set(modelData.url, gltf.scene);
          applyModel(gltf.scene);
        },
        undefined,
        (error) => {
          console.error(`Error loading GLB ${modelData.id}:`, error);
          isLoadingRef.current = false;
        },
      );
    };

    const handleMapMovement = () => {
      if (!isLoadedRef.current) {
        const zoom = map.getZoom();
        const bounds = map.getBounds();
        if (
          zoom >= LOAD_ZOOM_THRESHOLD &&
          bounds.contains(modelData.centerCoordinate)
        ) {
          load3DModel();
        }
        return;
      }

      if (movementRafRef.current) return;
      movementRafRef.current = requestAnimationFrame(() => {
        applyDynamicOpacity();
        movementRafRef.current = null;
      });
    };

    const addLayer = () => {
      if (!map.getLayer(modelData.layerId)) {
        map.addLayer(customLayer);
        handleMapMovement();
      }
    };

    if (map.isStyleLoaded()) addLayer();
    else map.once("style.load", addLayer);

    map.on("move", handleMapMovement);

    return () => {
      map.off("move", handleMapMovement);
      if (map.getLayer(modelData.layerId)) map.removeLayer(modelData.layerId);
      resetThreeState();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    map,
    properties?.basemap_used,
    applyDynamicOpacity,
    triggerRepaint,
    resetThreeState,
  ]);

  useEffect(() => {
    map?.triggerRepaint();
  }, [localTransform, map]);

  useEffect(() => {
    if (threeRef.current.model) {
      threeRef.current.model.visible = !isModelHidden;
      map?.triggerRepaint();
    }
  }, [isModelHidden, map]);

  return null;
};

const TAP_MOVE_THRESHOLD = 8;

const POLYGON_HITBOX = ({ map, isModelHidden, modelData, onHitboxClick }) => {
  const isModelHiddenRef = useRef(isModelHidden);
  const onHitboxClickRef = useRef(onHitboxClick);

  // Touch tracking — stored on the canvas native events, separate from MapLibre's
  // synthesised click so touch devices don't get double-fired or suppressed.
  const touchStartPosRef = useRef(null); // {x, y} of first touchstart
  const touchMovedRef = useRef(false); // true if finger moved beyond threshold
  const touchCountRef = useRef(0); // number of concurrent touches

  useEffect(() => {
    isModelHiddenRef.current = isModelHidden;
  }, [isModelHidden]);

  useEffect(() => {
    onHitboxClickRef.current = onHitboxClick;
  }, [onHitboxClick]);

  useEffect(() => {
    if (!map) return;

    const canvas = map.getCanvas();

    // ── Native touch listeners on the canvas ─────────────────────────────────
    // We track these ourselves because MapLibre's `click` event on touch
    // devices is synthesised from touchend and can race with our pointer
    // suppression logic, causing legitimate taps to be swallowed.

    const handleTouchStart = (e) => {
      touchCountRef.current = e.touches.length;

      if (e.touches.length === 1) {
        touchStartPosRef.current = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
        };
        touchMovedRef.current = false;
      } else {
        // Multi-touch (pinch/zoom) — cancel any pending tap
        touchStartPosRef.current = null;
        touchMovedRef.current = true;
      }
    };

    const handleTouchMove = (e) => {
      if (!touchStartPosRef.current) return;
      const dx = e.touches[0].clientX - touchStartPosRef.current.x;
      const dy = e.touches[0].clientY - touchStartPosRef.current.y;
      if (Math.sqrt(dx * dx + dy * dy) > TAP_MOVE_THRESHOLD) {
        touchMovedRef.current = true;
      }
    };

    const handleTouchEnd = () => {
      touchCountRef.current = Math.max(0, touchCountRef.current - 1);
    };

    const handleTouchCancel = () => {
      touchCountRef.current = 0;
      touchStartPosRef.current = null;
      touchMovedRef.current = true;
    };

    canvas.addEventListener("touchstart", handleTouchStart, { passive: true });
    canvas.addEventListener("touchmove", handleTouchMove, { passive: true });
    canvas.addEventListener("touchend", handleTouchEnd, { passive: true });
    canvas.addEventListener("touchcancel", handleTouchCancel, {
      passive: true,
    });

    // ── Hitbox source + layer ─────────────────────────────────────────────────
    const addHitboxLayer = () => {
      if (!map.getSource(modelData.hitboxSourceId)) {
        map.addSource(modelData.hitboxSourceId, {
          type: "geojson",
          data: {
            type: "Feature",
            geometry: { type: "Polygon", coordinates: modelData.hitboxPolygon },
          },
        });
      }
      if (!map.getLayer(modelData.hitboxLayerId)) {
        map.addLayer({
          id: modelData.hitboxLayerId,
          type: "fill",
          source: modelData.hitboxSourceId,
          paint: { "fill-color": "#000000", "fill-opacity": 0.01 },
        });
      }
    };

    if (map.isStyleLoaded()) addHitboxLayer();
    else map.once("style.load", addHitboxLayer);

    // ── MapLibre click (mouse) handler ────────────────────────────────────────
    // On desktop this fires reliably. On touch it fires too but we guard it
    // with the touch-moved flag so we don't double-fire with touchend below.
    const handleClick = (e) => {
      if (isModelHiddenRef.current) return;

      // If the event originated from a touch and the finger moved, ignore.
      if (touchMovedRef.current) {
        touchMovedRef.current = false;
        return;
      }

      // On touch devices, MapLibre fires `click` after `touchend`. We handle
      // the touch case via the `touchend` layer event below, so skip here when
      // a touch sequence just ended (touchStartPos still set means touchend
      // hasn't cleared it yet — but we clear it in the touchend handler above,
      // so by the time `click` fires touchStartPos is already null for touch).
      // Extra guard: if originalEvent is a TouchEvent, delegate to touchend path.
      if (
        e.originalEvent &&
        typeof TouchEvent !== "undefined" &&
        e.originalEvent instanceof TouchEvent
      ) {
        return;
      }

      if (e?.preventDefault) e.preventDefault();
      onHitboxClickRef.current?.();
    };

    // ── MapLibre touchend layer event (touch devices) ─────────────────────────
    // MapLibre emits this when a finger lifts over a layer. Use it instead of
    // the synthesised `click` for touch so there is no delay and no conflict.
    const handleTouchEndLayer = (e) => {
      if (isModelHiddenRef.current) return;
      if (touchMovedRef.current) {
        touchMovedRef.current = false;
        return;
      }
      if (e?.preventDefault) e.preventDefault();
      onHitboxClickRef.current?.();
    };

    const handleMouseEnter = () => {
      if (!isModelHiddenRef.current) map.getCanvas().style.cursor = "pointer";
    };

    const handleMouseLeave = () => {
      map.getCanvas().style.cursor = "";
    };

    map.on("click", modelData.hitboxLayerId, handleClick);
    map.on("touchend", modelData.hitboxLayerId, handleTouchEndLayer);
    map.on("mouseenter", modelData.hitboxLayerId, handleMouseEnter);
    map.on("mouseleave", modelData.hitboxLayerId, handleMouseLeave);

    return () => {
      canvas.removeEventListener("touchstart", handleTouchStart);
      canvas.removeEventListener("touchmove", handleTouchMove);
      canvas.removeEventListener("touchend", handleTouchEnd);
      canvas.removeEventListener("touchcancel", handleTouchCancel);

      if (map.getLayer(modelData.hitboxLayerId))
        map.removeLayer(modelData.hitboxLayerId);
      if (map.getSource(modelData.hitboxSourceId))
        map.removeSource(modelData.hitboxSourceId);

      map.off("click", modelData.hitboxLayerId, handleClick);
      map.off("touchend", modelData.hitboxLayerId, handleTouchEndLayer);
      map.off("mouseenter", modelData.hitboxLayerId, handleMouseEnter);
      map.off("mouseleave", modelData.hitboxLayerId, handleMouseLeave);
    };
  }, [map, modelData]);

  return null;
};

const POPUP_FLAT = ({ map, showPopup, onClose, onHide3D, modelData }) => {
  const popupRef = useRef(null);
  const [isPositioned, setIsPositioned] = useState(false);

  const popupCoordinate = useMemo(() => {
    if (!modelData.hitboxPolygon || !modelData.hitboxPolygon.length) {
      return modelData.centerCoordinate;
    }

    try {
      const ring = Array.isArray(modelData.hitboxPolygon[0][0])
        ? modelData.hitboxPolygon[0]
        : modelData.hitboxPolygon;

      let minLng = Infinity,
        maxLng = -Infinity;
      let minLat = Infinity,
        maxLat = -Infinity;

      ring.forEach(([lng, lat]) => {
        if (lng < minLng) minLng = lng;
        if (lng > maxLng) maxLng = lng;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
      });

      return [(minLng + maxLng) / 2, (minLat + maxLat) / 2];
    } catch (error) {
      console.warn(
        "Could not calculate hitbox centroid, falling back to centerCoordinate.",
        error,
      );
      return modelData.centerCoordinate;
    }
  }, [modelData.hitboxPolygon, modelData.centerCoordinate]);

  useEffect(() => {
    if (!showPopup || !map) {
      setIsPositioned(false);
      return;
    }

    const updatePopupPosition = () => {
      if (!popupRef.current) return;

      const screenPos = map.project(popupCoordinate);
      const el = popupRef.current;
      el.style.transform = `translate(${screenPos.x - el.offsetWidth / 2}px, ${
        screenPos.y - el.offsetHeight - 12
      }px)`;

      if (!isPositioned) {
        requestAnimationFrame(() => setIsPositioned(true));
      }
    };

    updatePopupPosition();
    map.on("move", updatePopupPosition);

    return () => map.off("move", updatePopupPosition);
  }, [showPopup, map, popupCoordinate, isPositioned]);

  if (!showPopup) return null;

  return (
    <div
      ref={popupRef}
      style={{
        position: "absolute",
        top: "0px",
        left: "0px",
        background: "#ffffff",
        borderRadius: "16px",
        boxShadow:
          "0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
        border: "1px solid rgba(0, 0, 0, 0.04)",
        minWidth: "240px",
        maxWidth: "280px",
        pointerEvents: "auto",
        padding: "20px",
        zIndex: 10,
        opacity: isPositioned ? 1 : 0,
        transition: "opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <button
        onClick={onClose}
        style={{
          position: "absolute",
          top: "12px",
          right: "12px",
          cursor: "pointer",
          padding: "6px",
          border: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "opacity 0.2s ease",
        }}
        aria-label="Close popup"
        className="button_circle background_black"
      >
        ✕
      </button>

      <main
        style={{
          maxHeight: "277px",
          overflowY: "auto",
          marginTop: "12px",
          paddingRight: "6px",
          scrollbarWidth: "thin",
        }}
      >
        <button
          className="button hover_primary margin_bottom_extra margin_right"
          onClick={onHide3D}
        >
          Hide 3D
        </button>

        <p className="text_medium sub_color margin_bottom">Building name</p>
        <p className="text_bold margin_bottom_extra">
          {modelData.popupData.buildingName}
        </p>

        <p className="text_medium sub_color margin_bottom">Artist</p>
        <p className="text_bold">{modelData.popupData.artist}</p>
      </main>

      <div
        style={{
          position: "absolute",
          bottom: "-11px",
          left: "50%",
          transform: "translateX(-50%)",
          width: 0,
          height: 0,
          borderLeft: "12px solid transparent",
          borderRight: "12px solid transparent",
          borderTop: "12px solid #ffffff",
          filter: "drop-shadow(0 4px 3px rgba(0, 0, 0, 0.08))",
          pointerEvents: "none",
        }}
      />
    </div>
  );
};

const MODEL_PARENT = ({
  map,
  properties,
  modelData,
  activePopupId,
  setActivePopup,
}) => {
  const [isModelHidden, setIsModelHidden] = useState(false);
  const hasReachedResetZoomRef = useRef(false);
  const lowestZoomSinceHiddenRef = useRef(Infinity);

  const isPopupActive = activePopupId === modelData.id;

  const handleHitboxClick = useCallback(() => {
    setActivePopup(modelData.id);
  }, [modelData.id, setActivePopup]);

  useEffect(() => {
    if (!map || !isModelHidden) {
      hasReachedResetZoomRef.current = false;
      lowestZoomSinceHiddenRef.current = Infinity;
      return;
    }

    const handleZoomCheck = () => {
      const zoom = map.getZoom();

      if (zoom < lowestZoomSinceHiddenRef.current) {
        lowestZoomSinceHiddenRef.current = zoom;
      }
      if (lowestZoomSinceHiddenRef.current <= RESET_ZOOM_THRESHOLD) {
        hasReachedResetZoomRef.current = true;
      }
      if (!hasReachedResetZoomRef.current) return;

      if (zoom >= LOAD_ZOOM_THRESHOLD) {
        requestAnimationFrame(() => {
          if (!map) return;
          if (map.getBounds().contains(modelData.centerCoordinate)) {
            setIsModelHidden(false);
            hasReachedResetZoomRef.current = false;
            lowestZoomSinceHiddenRef.current = Infinity;
          }
        });
      }
    };

    map.on("zoom", handleZoomCheck);
    return () => map.off("zoom", handleZoomCheck);
  }, [map, isModelHidden, modelData.centerCoordinate]);

  return (
    <>
      <MODEL_3D
        map={map}
        isModelHidden={isModelHidden}
        properties={properties}
        modelData={modelData}
      />
      <POLYGON_HITBOX
        map={map}
        isModelHidden={isModelHidden}
        modelData={modelData}
        onHitboxClick={handleHitboxClick}
      />
      <POPUP_FLAT
        map={map}
        showPopup={isPopupActive && !isModelHidden}
        onClose={() => setActivePopup(null)}
        onHide3D={() => {
          setIsModelHidden(true);
          setActivePopup(null);
        }}
        modelData={modelData}
      />
    </>
  );
};

const LIBRE_GLB = ({ layer, properties }) => {
  const map = layer?.map_object;
  const [activePopupId, setActivePopup] = useState(null);

  if (!map) return null;

  return (
    <>
      {MODELS_DATA.map((modelData) => (
        <MODEL_PARENT
          key={modelData.id}
          map={map}
          properties={properties}
          modelData={modelData}
          activePopupId={activePopupId}
          setActivePopup={setActivePopup}
        />
      ))}
    </>
  );
};

const mapStateToProps = (state) => ({
  layer: state.layer,
  properties: state.properties,
});

export default connect(mapStateToProps)(LIBRE_GLB);
